import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { publicEliteNetwork } from '../lib/api';

/**
 * KINGDM-inspired "Access The Elite Network" section.
 * Two infinite marquee rows in opposite directions.
 *
 * - If admin has uploaded images via the Admin → "Elite Network" tab,
 *   we render those images in the same premium gold-framed marquee.
 * - Otherwise, we fall back to a curated set of Unsplash CDN images so
 *   the section never looks empty out of the box.
 */

// Curated luxury / trading / mentorship lifestyle imagery (Unsplash CDN, public/free).
const FALLBACK_ROW_A = [
  'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=900&q=80&auto=format&fit=crop', // luxury sports car
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=900&q=80&auto=format&fit=crop', // city skyline at night
  'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=900&q=80&auto=format&fit=crop', // trader monitors
  'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&q=80&auto=format&fit=crop',  // business meeting
  'https://images.unsplash.com/photo-1518544801976-3e159e50e5bb?w=900&q=80&auto=format&fit=crop', // luxury watch on wrist
  'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=900&q=80&auto=format&fit=crop',  // trader looking at charts
  'https://images.unsplash.com/photo-1620228885847-9eab2a1adddc?w=900&q=80&auto=format&fit=crop', // luxurious lifestyle
  'https://images.unsplash.com/photo-1589330273594-fade1ee91647?w=900&q=80&auto=format&fit=crop', // black mclaren
  'https://images.unsplash.com/photo-1591696205602-2f950c417cb9?w=900&q=80&auto=format&fit=crop', // hands shaking - mentorship
];

const FALLBACK_ROW_B = [
  'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=900&q=80&auto=format&fit=crop', // luxury watch close-up
  'https://images.unsplash.com/photo-1616514197671-15d99ce7a6f8?w=900&q=80&auto=format&fit=crop', // tech setup gaming/trading
  'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=900&q=80&auto=format&fit=crop', // gold watch
  'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&q=80&auto=format&fit=crop',  // group of business men
  'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=900&q=80&auto=format&fit=crop',  // dollar bills cash
  'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=900&q=80&auto=format&fit=crop', // private jet
  'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=900&q=80&auto=format&fit=crop', // forex screens
  'https://images.unsplash.com/photo-1520637836862-4d197d17c25a?w=900&q=80&auto=format&fit=crop', // luxury suit men
  'https://images.unsplash.com/photo-1603969072881-b0fc7f3d77d7?w=900&q=80&auto=format&fit=crop', // fine watch + cigar
];

function MarqueeRow({ items, direction = 'left', durationSec = 60 }) {
  // Duplicate the array so the animation loops seamlessly
  const looped = [...items, ...items];
  const animateX = direction === 'left' ? ['0%', '-50%'] : ['-50%', '0%'];

  return (
    <div className="relative overflow-hidden">
      {/* Edge masks for premium fade */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10 bg-gradient-to-r from-[hsl(0_0%_4%)] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-[hsl(0_0%_4%)] to-transparent" />

      <motion.div
        className="flex gap-4 w-max"
        animate={{ x: animateX }}
        transition={{ ease: 'linear', duration: durationSec, repeat: Infinity }}
      >
        {looped.map((it, i) => (
          <div
            key={`${direction}-${i}`}
            className="relative shrink-0 w-[260px] sm:w-[320px] md:w-[380px] aspect-[4/5] rounded-2xl overflow-hidden gold-hairline bg-[hsl(240_6%_8%)]"
          >
            <img
              src={it.image_url}
              alt=""
              loading="lazy"
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Bottom gradient overlay for depth */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            {/* Tiny brand chip */}
            <div className="absolute bottom-3 left-3 px-2 py-1 rounded-full bg-black/55 backdrop-blur-sm text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)] font-medium max-w-[80%] truncate">
              {it.caption || 'ALVEETRADE NETWORK'}
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export default function EliteNetwork() {
  // null = loading; [] = none uploaded yet (use fallback); [...] = real admin uploads
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancel = false;
    publicEliteNetwork()
      .then((d) => { if (!cancel) setItems(d.items || []); })
      .catch(() => { if (!cancel) setItems([]); });
    return () => { cancel = true; };
  }, []);

  // Decide what to render
  const useUploads = Array.isArray(items) && items.length > 0;
  let rowA;
  let rowB;
  if (useUploads) {
    if (items.length === 1) {
      rowA = items;
      rowB = items;
    } else {
      const mid = Math.ceil(items.length / 2);
      rowA = items.slice(0, mid);
      rowB = items.slice(mid).length ? items.slice(mid) : items.slice(0, mid);
    }
  } else {
    // Wrap fallback URLs into the same { image_url } shape as admin uploads
    rowA = FALLBACK_ROW_A.map((u) => ({ image_url: u }));
    rowB = FALLBACK_ROW_B.map((u) => ({ image_url: u }));
  }

  return (
    <section
      id="elite-network"
      data-testid="elite-network-section"
      className="section-pad bg-[hsl(0_0%_4%)] border-t border-b border-border relative overflow-hidden"
    >
      {/* Subtle radial accent */}
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 0%, rgba(245,185,66,0.08), transparent 70%)',
        }}
      />

      <div className="container-x relative">
        <div className="max-w-3xl mx-auto text-center mb-6 sm:mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] mb-3">
            The Inner Circle
          </div>
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
            ACCESS THE ELITE NETWORK
          </h2>
          <p className="text-muted-foreground mt-4 text-base md:text-lg leading-relaxed">
            Trading can be a lonely journey, but it doesn't have to be. Immerse yourself in an
            environment of <span className="text-white font-medium">7-figure mentors</span> and{' '}
            <span className="text-white font-medium">high-performance traders</span>. When you
            normalize success, execution becomes automatic.
          </p>
        </div>
      </div>

      {/* Full-bleed marquees */}
      <div className="space-y-4 sm:space-y-6">
        <MarqueeRow items={rowA} direction="left" durationSec={70} />
        <MarqueeRow items={rowB} direction="right" durationSec={85} />
      </div>

      {/* Stats strip below */}
      <div className="container-x relative mt-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
          {[
            { v: '131K+', l: 'Members' },
            { v: '40+', l: 'Countries' },
            { v: '24/7', l: 'Live Calls' },
            { v: '7-Fig', l: 'Mentors' },
          ].map((s) => (
            <div
              key={s.l}
              className="rounded-2xl card-surface p-4 sm:p-6 text-center gold-hairline"
            >
              <div className="font-display uppercase text-2xl sm:text-3xl text-[hsl(43_92%_56%)]">
                {s.v}
              </div>
              <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
